import { activityApiService } from "../activity-service";
import { getCurrentUserInfo } from "@/utils/userInfo";
import { store } from "@/lib/redux/store";

/**
 * Format date to dd/mm/yyyy as required by the backend
 */
const formatDateToDDMMYYYY = (date: Date): string => {
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}/${month}/${year}`;
};

/**
 * Get registration activities (activity responses) for a specific activity
 * Uses the activity base URL as mentioned in the requirements
 * 
 * @param activityId - Activity ID to fetch responses for
 * @param startDate - Optional start date for filtering responses (used for export)
 * @param endDate - Optional end date for filtering responses (used for export)
 * @param params - Additional query parameters (pagination, search, sort)
 */
export const getActivityResponses = async (
  activityId: string,
  startDate?: Date,
  endDate?: Date,
  params?: {
    page?: number;
    pageSize?: number;
    search?: string;
    sort?: string;
  }
) => {
  // Get actual user ID and tenant ID from store
  const { user_id } = getCurrentUserInfo();
  const state = store.getState();

  // Build query parameters - only add dates if provided
  const queryParams: Record<string, unknown> = {};
  
  if (startDate && endDate) {
    queryParams.start_date = formatDateToDDMMYYYY(startDate);
    queryParams.end_date = formatDateToDDMMYYYY(endDate);
  }
  
  // Note: page and pageSize are handled client-side, not sent to API
  if (params?.search) queryParams.search = params.search;
  if (params?.sort) queryParams.sort = params.sort;

  // Build headers with required user-id
  // Note: X-Tenant-Id is automatically added by the request interceptor
  const headers: Record<string, string> = {
    'user-id': user_id,
  };

  const response = await activityApiService({
    url: `/activity-response/${activityId}/all-responses`,
    method: "get",
    protectedRoute: true,
    params: Object.keys(queryParams).length > 0 ? queryParams : undefined,
    headers,
  });
  return response;
};

/**
 * Get a specific activity response by ID
 */
export const getActivityResponse = async (activityResponseId: string) => {
  const response = await activityApiService({
    url: `/activity-response/response/${activityResponseId}`,
    method: "get",
    protectedRoute: true,
  });
  return response;
};

